import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
  Checkbox,
  Drawer,
  IconButton,
  Divider,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha,
  Collapse,
} from '@mui/material';
import {
  WarningAmber as WarningAmberIcon,
  LocalOfferOutlined as LocalOfferOutlinedIcon,
  Search as SearchIcon,
  VerifiedUserOutlined as VerifiedUserOutlinedIcon,
  EmailOutlined as EmailOutlinedIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  FilterList as FilterListIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  CloudUpload as CloudUploadIcon,
  InfoOutlined as InfoOutlinedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  RotateLeft as RotateLeftIcon,
  Link as LinkIcon,
  ErrorOutline as ErrorOutlineIcon,
  Refresh as RefreshIcon,
  BoltOutlined as BoltOutlinedIcon,
  LocalFireDepartmentOutlined as LocalFireDepartmentOutlinedIcon,
  WaterDropOutlined as WaterDropOutlinedIcon,
  LayersOutlined as LayersOutlinedIcon,
  FiberManualRecord as FiberManualRecordIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectPilotList, selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { useToast } from 'context/ToastContext';
import { useGlobalConfig } from 'context/GlobalContext';
import { MediaAPI, envMatrixConfigsToCompareUrls, isAcceptedMediaFile } from 'apis/mediaAPI';
import { PilotApis } from 'apis/pilotAPI';
import type { MediaImagesCounts } from 'types/apis/mediaInterfaces';
import type { MediaEnvironment } from 'types/apis/mediaInterfaces';
import type { ProjectEnvironment } from 'types/apis/pilotInterfaces';
import {
  type MediaAssetVersion,
  type JourneyTab,
} from './mediaAssetWorkflow';
import {
  type AppEnvironment,
  formatAppEnvLabel,
  getComparisonEnvOrder,
  getDrawerEnvironmentsMissingApiHost,
  getProjectEnvironmentApiHost,
  getProjectEnvironmentDisplayLabel,
  getPromotionDropdownEnvironments,
  getPromoteTargets,
  isDrawerEnvironmentAvailable,
  mapAssociatedEnvironmentsByAppEnv,
  projectEnvironmentToAppEnv,
  resolveAppEnvironment,
  toMediaApiEnvironment,
  toMediaApiEnvironmentParam,
} from './mediaEnvMapping';

type ViewMode = 'snapshot' | 'compare';

const APP_ENV = resolveAppEnvironment();
const CURRENT_MEDIA_ENV = toMediaApiEnvironment(APP_ENV);
const MISSING_DRAWER_ENV_MESSAGE = 'Please add required env to use this feature';
const CONFIG_VALUE_NOT_FOUND_MESSAGE = 'Config value not found';

const JOURNEY_TABS: { id: JourneyTab; label: string; icon: React.ReactElement; countKey: keyof MediaImagesCounts }[] = [
  { id: 'brand', label: 'Brand Guidelines', icon: <VerifiedUserOutlinedIcon fontSize="small" />, countKey: 'brand_guidelines' },
  { id: 'emails', label: 'Emails', icon: <EmailOutlinedIcon fontSize="small" />, countKey: 'email' },
  { id: 'web', label: 'Web Dashboard', icon: <DashboardOutlinedIcon fontSize="small" />, countKey: 'dashboard' },
  { id: 'her', label: 'HER', icon: <DescriptionOutlinedIcon fontSize="small" />, countKey: 'her' },
];

const getDrawerEnvImageUrl = (
  env: AppEnvironment,
  currentAppEnv: AppEnvironment,
  drawerEnvUrls: Partial<Record<AppEnvironment, string>>,
  fallbackUrl?: string,
): string | undefined => {
  if (env in drawerEnvUrls) {
    return drawerEnvUrls[env];
  }
  if (env === currentAppEnv) {
    return fallbackUrl;
  }
  return undefined;
};

const drawerEnvHasDrift = (
  env: AppEnvironment,
  currentAppEnv: AppEnvironment,
  drawerEnvUrls: Partial<Record<AppEnvironment, string>>,
  baselineUrl: string,
): boolean => {
  if (env === currentAppEnv || !baselineUrl) return false;

  const envUrl = getDrawerEnvImageUrl(env, currentAppEnv, drawerEnvUrls, baselineUrl);
  if (envUrl === undefined) return false;
  if (envUrl === '') return true;

  return envUrl !== baselineUrl;
};

const drawerEnvConnectorHasDrift = (
  leftEnv: AppEnvironment,
  rightEnv: AppEnvironment,
  currentAppEnv: AppEnvironment,
  drawerEnvUrls: Partial<Record<AppEnvironment, string>>,
  fallbackUrl?: string,
): boolean => {
  const leftUrl = getDrawerEnvImageUrl(leftEnv, currentAppEnv, drawerEnvUrls, fallbackUrl);
  const rightUrl = getDrawerEnvImageUrl(rightEnv, currentAppEnv, drawerEnvUrls, fallbackUrl);

  if (leftUrl === undefined || rightUrl === undefined) return false;
  if (leftUrl === '' || rightUrl === '') return true;

  return leftUrl !== rightUrl;
};

const assetHasEnvDriftFromUrls = (
  baselineUrl: string,
  currentAppEnv: AppEnvironment,
  envUrls: Partial<Record<AppEnvironment, string>>,
  envOrder: AppEnvironment[],
): boolean =>
  envOrder.some((env) => drawerEnvHasDrift(env, currentAppEnv, envUrls, baselineUrl));

const isAllCategory = (category: string) => category.toUpperCase() === 'ALL';

const getCategoryDisplayLabel = (category: string) =>
  isAllCategory(category) ? 'Other Images' : category;

const CollapsibleCategorySection: React.FC<{
  category: string;
  expanded: boolean;
  onToggle: () => void;
  showOnlyDrift?: boolean;
  count?: number;
  subtitle?: React.ReactNode;
  headerActions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ category, expanded, onToggle, showOnlyDrift, count, subtitle, headerActions, children }) => {
  const label = getCategoryDisplayLabel(category);

  return (
    <Box sx={{ mb: 6 }}>
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 2,
          mb: expanded ? 3 : 0,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
          <ExpandMoreIcon
            sx={{
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 0.2s',
              color: 'text.secondary',
            }}
          />
          <FiberManualRecordIcon sx={{ fontSize: 10, color: showOnlyDrift ? '#f59e0b' : 'primary.main' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{
                textTransform: isAllCategory(category) ? 'none' : 'uppercase',
                letterSpacing: '-0.02em',
              }}
            >
              {label}
            </Typography>
            {subtitle}
          </Box>
          {count !== undefined && (
            <Chip label={count} size="small" variant="outlined" sx={{ fontWeight: 700, ml: 0.5 }} />
          )}
        </Box>
        {headerActions && (
          <Box onClick={(e) => e.stopPropagation()} sx={{ ml: 2, flexShrink: 0 }}>
            {headerActions}
          </Box>
        )}
      </Box>
      <Collapse in={expanded}>{children}</Collapse>
    </Box>
  );
};

const ComparisonRow: React.FC<{
  asset: MediaAssetVersion;
  currentAppEnv: AppEnvironment;
  envUrls?: Partial<Record<AppEnvironment, string>>;
  onPromote: (target: AppEnvironment, asset: MediaAssetVersion) => void;
}> = ({ asset, currentAppEnv, envUrls = {}, onPromote }) => {
  const theme = useTheme();
  const baselineUrl = asset.fileURL ?? '';
  const envOrder = getComparisonEnvOrder(currentAppEnv);
  const isOutOfSync = assetHasEnvDriftFromUrls(baselineUrl, currentAppEnv, envUrls, envOrder);
  const promoteTargets = new Set(getPromoteTargets(currentAppEnv));

  const gridColumns = [
    '1.2fr',
    ...envOrder.flatMap((_, idx) => (idx < envOrder.length - 1 ? ['180px', '60px'] : ['180px'])),
  ].join(' ');

  const getColumnLabel = (env: AppEnvironment, idx: number): string | undefined => {
    if (idx === 0) return `V${asset.versionNumber}.0`;
    if (env === 'prod') return 'Published';
    return undefined;
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: gridColumns,
        gap: 1,
        alignItems: 'center',
        p: 2,
        borderRadius: 3,
        border: 1,
        borderColor: isOutOfSync ? '#fcd34d' : 'divider',
        bgcolor: isOutOfSync ? alpha('#f59e0b', 0.06) : 'background.paper',
      }}
    >
      <Box sx={{ pr: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.1em', color: 'text.secondary', textTransform: 'uppercase' }}>
            {asset.category}
          </Typography>
          {isOutOfSync && (
            <Chip label="Drift" size="small" sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: alpha('#f59e0b', 0.15), color: '#b45309', border: '1px solid #fcd34d' }} />
          )}
        </Box>
        <Typography variant="body2" fontWeight={700}>{asset.name}</Typography>
        <Typography component="code" variant="caption" sx={{ fontFamily: 'monospace', color: 'primary.main', opacity: 0.7, bgcolor: alpha(theme.palette.grey[100], 1), px: 0.75, py: 0.25, borderRadius: 0.5, border: 1, borderColor: 'divider' }}>
          {asset.registryMapping}
        </Typography>
      </Box>

      {envOrder.map((env, idx) => {
        const isPromoteTarget = promoteTargets.has(env);
        const isDriftColumn = drawerEnvHasDrift(env, currentAppEnv, envUrls, baselineUrl);
        const leftEnv = envOrder[idx - 1];
        const syncDrift =
          idx > 0 && leftEnv != null
            ? drawerEnvConnectorHasDrift(leftEnv, env, currentAppEnv, envUrls, baselineUrl)
            : false;
        const syncTooltip =
          idx === 0 && currentAppEnv === 'uat'
            ? 'UAT diff from Non-Prod'
            : `${formatAppEnvLabel(env)} diff from ${formatAppEnvLabel(leftEnv ?? currentAppEnv)}`;

        return (
          <React.Fragment key={env}>
            {idx > 0 && <SyncIndicator isDrift={syncDrift} tooltip={syncDrift ? syncTooltip : undefined} />}
            <EnvColumn
              imageUrl={getDrawerEnvImageUrl(env, currentAppEnv, envUrls, baselineUrl)}
              label={getColumnLabel(env, idx)}
              isDrift={isDriftColumn && isPromoteTarget}
              promoteLabel={isPromoteTarget ? 'Promote' : undefined}
              onPromote={isPromoteTarget ? () => onPromote(env, asset) : undefined}
            />
          </React.Fragment>
        );
      })}
    </Box>
  );
};

const EnvColumn: React.FC<{
  imageUrl?: string;
  label?: string;
  isDrift?: boolean;
  promoteLabel?: string;
  onPromote?: () => void;
}> = ({ imageUrl, label, isDrift, promoteLabel, onPromote }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
    <Box
      sx={{
        height: 80,
        width: 128,
        borderRadius: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 1.5,
        border: 1,
        borderColor: isDrift ? '#fcd34d' : 'divider',
        bgcolor: isDrift ? alpha('#f59e0b', 0.08) : alpha('#f1f5f9', 1),
        overflow: 'hidden',
      }}
    >
      {imageUrl ? (
        <Box component="img" src={imageUrl} alt="" sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', filter: isDrift ? 'grayscale(1)' : 'none', opacity: isDrift ? 0.4 : 1 }} />
      ) : (
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 9, fontWeight: 700, textAlign: 'center' }}>
          Not found
        </Typography>
      )}
    </Box>
    {promoteLabel && isDrift ? (
      <Button size="small" variant="contained" onClick={onPromote} sx={{ height: 24, px: 1.5, fontSize: 9, fontWeight: 800, textTransform: 'uppercase', bgcolor: '#d97706', '&:hover': { bgcolor: '#b45309' } }}>
        {promoteLabel}
      </Button>
    ) : (
      <Chip label={label || 'In Sync'} size="small" variant="outlined" sx={{ height: 18, fontSize: 9, fontWeight: 800 }} />
    )}
  </Box>
);

const SyncIndicator: React.FC<{ isDrift: boolean; tooltip?: string }> = ({ isDrift, tooltip }) => {
  const content = (
    <Box
      sx={{
        height: 32,
        width: 32,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 1,
        borderColor: isDrift ? '#fcd34d' : '#86efac',
        bgcolor: isDrift ? alpha('#f59e0b', 0.1) : alpha('#22c55e', 0.1),
        color: isDrift ? '#f59e0b' : '#16a34a',
        ...(isDrift && { animation: 'pulse 2s infinite' }),
        '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.6 } },
      }}
    >
      {isDrift ? <ErrorOutlineIcon fontSize="small" /> : <LinkIcon fontSize="small" />}
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip}>{content}</Tooltip> : content;
};

const ApplianceGrid: React.FC<{
  category: string;
  items: MediaAssetVersion[];
  viewMode: ViewMode;
  showOnlyDrift: boolean;
  currentAppEnv: AppEnvironment;
  assetsWithEnvDrift: Set<string>;
  compareAssetEnvUrls: Record<string, Partial<Record<AppEnvironment, string>>>;
  onSelect: (asset: MediaAssetVersion) => void;
  onToast: (title: string, description?: string) => void;
  onPromote: (target: AppEnvironment, asset: MediaAssetVersion) => void;
}> = ({ items, viewMode, showOnlyDrift, currentAppEnv, assetsWithEnvDrift, compareAssetEnvUrls, onSelect, onPromote }) => {
  const theme = useTheme();
  const driftItems = items.filter((a) => a.assetId && assetsWithEnvDrift.has(a.assetId));
  if (showOnlyDrift && driftItems.length === 0) return null;
  const displayItems = showOnlyDrift ? driftItems : items;

  return (
    <>
      {viewMode === 'snapshot' ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)', xl: 'repeat(7, 1fr)' }, gap: 2 }}>
          {displayItems.map((asset) => (
            <Card
              key={asset.id}
              variant="outlined"
              onClick={() => onSelect(asset)}
              sx={{
                cursor: 'pointer',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 140,
                p: 2,
                '&:hover': { borderColor: 'primary.main', boxShadow: 2 },
              }}
            >
              <Box sx={{ height: 64, width: 64, bgcolor: alpha(theme.palette.grey[100], 1), borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <Box component="img" src={asset.thumbnailURL} alt={asset.name} sx={{ height: 32, width: 32, objectFit: 'contain', opacity: 0.7 }} />
              </Box>
              <Typography variant="caption" fontWeight={700} textAlign="center" sx={{ maxWidth: 100 }}>
                {asset.name}
              </Typography>
            </Card>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {displayItems.map((asset) => (
            <ComparisonRow
              key={asset.id}
              asset={asset}
              currentAppEnv={currentAppEnv}
              envUrls={asset.assetId ? compareAssetEnvUrls[asset.assetId] : undefined}
              onPromote={(target) => onPromote(target, asset)}
            />
          ))}
        </Box>
      )}
    </>
  );
};

const MascotMatrix: React.FC<{
  category: string;
  items: MediaAssetVersion[];
  viewMode: ViewMode;
  showOnlyDrift: boolean;
  currentAppEnv: AppEnvironment;
  assetsWithEnvDrift: Set<string>;
  compareAssetEnvUrls: Record<string, Partial<Record<AppEnvironment, string>>>;
  onSelect: (asset: MediaAssetVersion) => void;
  onPromote: (target: AppEnvironment, asset: MediaAssetVersion) => void;
}> = ({ items, viewMode, showOnlyDrift, currentAppEnv, assetsWithEnvDrift, compareAssetEnvUrls, onSelect, onPromote }) => {
  const theme = useTheme();
  const driftItems = items.filter((a) => a.assetId && assetsWithEnvDrift.has(a.assetId));
  if (showOnlyDrift && driftItems.length === 0) return null;

  const userTypes = [
    { id: 'electric', label: 'Electric', sub: 'Power consumers', icon: <BoltOutlinedIcon />, color: alpha('#f59e0b', 0.15) },
    { id: 'gas', label: 'Gas', sub: 'Natural gas', icon: <LocalFireDepartmentOutlinedIcon />, color: alpha('#f97316', 0.15) },
    { id: 'water', label: 'Water', sub: 'Water utility', icon: <WaterDropOutlinedIcon />, color: alpha('#3b82f6', 0.15) },
    { id: 'solar', label: 'Solar', sub: 'Net generation', icon: <LayersOutlinedIcon />, color: alpha('#6366f1', 0.15) },
  ];

  const statuses = [
    { id: 'good', label: 'GOOD', badge: 'BELOW NEIGHBORS', desc: 'Used less energy than similar homes.', color: alpha('#22c55e', 0.2) },
    { id: 'fair', label: 'FAIR', badge: 'SIMILAR TO NEIGHBORS', desc: 'In line with similar homes.', color: alpha('#3b82f6', 0.2) },
    { id: 'great', label: 'GREAT', badge: 'WELL BELOW NEIGHBORS', desc: 'Used significantly less energy — celebrate!', color: alpha('#10b981', 0.2) },
  ];

  return (
    <>
      {viewMode === 'snapshot' && !showOnlyDrift ? (
        <Card variant="outlined" sx={{ overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
              <TableRow>
                <TableCell sx={{ width: 200, borderRight: 1, borderColor: 'divider', fontWeight: 800, fontSize: 11, letterSpacing: '0.08em', color: 'text.secondary' }}>User Type</TableCell>
                {statuses.map((s) => (
                  <TableCell key={s.id} sx={{ borderRight: 1, borderColor: 'divider', verticalAlign: 'top' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="subtitle2" fontWeight={800}>{s.label}</Typography>
                      <Chip label={s.badge} size="small" sx={{ height: 20, fontSize: 9, fontWeight: 800, bgcolor: s.color }} />
                    </Box>
                    <Typography variant="caption" color="text.secondary">{s.desc}</Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {userTypes.map((type) => (
                <TableRow key={type.id}>
                  <TableCell sx={{ borderRight: 1, borderColor: 'divider', verticalAlign: 'top', pt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ height: 40, width: 40, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: type.color }}>{type.icon}</Box>
                      <Box>
                        <Typography variant="body2" fontWeight={700}>{type.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{type.sub}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  {statuses.map((status) => {
                    const asset = items.find(
                      (a) => a.assetId.toLowerCase().includes(type.id) && a.assetId.toLowerCase().includes(status.id),
                    );
                    return (
                      <TableCell key={status.id} sx={{ borderRight: '1', borderColor: 'divider', verticalAlign: 'top' }}>
                        {asset ? (
                          <Card variant="outlined" onClick={() => onSelect(asset)} sx={{ borderRadius: 1, cursor: 'pointer', '&:hover': { borderColor: 'primary.main', boxShadow: 2 } }}>
                            <Box sx={{ aspectRatio: '3/2', bgcolor: alpha(theme.palette.grey[100], 1), display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, position: 'relative' }}>
                              <Box component="img" src={asset.thumbnailURL} alt={asset.name} sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} />
                            </Box>
                            <CardContent sx={{ py: 1, textAlign: 'center', '&:last-child': { pb: 1 } }}>
                              <Typography variant="caption" fontWeight={700} noWrap>{asset.name}</Typography>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>{asset.dimensions}</Typography>
                            </CardContent>
                          </Card>
                        ) : (
                          <Box sx={{ aspectRatio: '3/2', border: '2px dashed', borderColor: 'divider', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" color="text.disabled" fontWeight={700}>No Asset</Typography>
                          </Box>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {(showOnlyDrift ? driftItems : items).map((asset) => (
            <ComparisonRow
              key={asset.id}
              asset={asset}
              currentAppEnv={currentAppEnv}
              envUrls={asset.assetId ? compareAssetEnvUrls[asset.assetId] : undefined}
              onPromote={(target) => onPromote(target, asset)}
            />
          ))}
        </Box>
      )}
    </>
  );
};

export const MediaContentPage: React.FC = () => {
  const theme = useTheme();
  const toast = useToast();
  const { rc } = useGlobalConfig();
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const pilotList = useSelector(selectPilotList) as { projectId?: string; name?: string }[];

  const [activeJourney, setActiveJourney] = useState<JourneyTab>('brand');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const currentAppEnv = APP_ENV;
  const [viewMode, setViewMode] = useState<ViewMode>('snapshot');
  const [search, setSearchTerm] = useState('');
  const [selectedAsset, setSelectedAsset] = useState<MediaAssetVersion | null>(null);
  const [showOnlyDrift, setShowOnlyDrift] = useState(false);
  const [compareAssetEnvUrls, setCompareAssetEnvUrls] = useState<Record<string, Partial<Record<AppEnvironment, string>>>>({});
  const [assetsWithEnvDrift, setAssetsWithEnvDrift] = useState<Set<string>>(new Set());
  const [isLoadingEnvDiff, setIsLoadingEnvDiff] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [assets, setAssets] = useState<MediaAssetVersion[]>([]);
  const [journeyCounts, setJourneyCounts] = useState<MediaImagesCounts | null>(null);
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);
  const failedFetchKeyRef = useRef<string | null>(null);
  const isFetchingRef = useRef(false);
  const uploadFileInputRef = useRef<HTMLInputElement>(null);
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [isUploading, setIsUploading] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [drawerEnvUrls, setDrawerEnvUrls] = useState<Partial<Record<AppEnvironment, string>>>({});
  const [isLoadingDrawerEnvUrls, setIsLoadingDrawerEnvUrls] = useState(false);
  const [associatedEnvironments, setAssociatedEnvironments] = useState<ProjectEnvironment[]>([]);
  const [isLoadingAssociatedEnvs, setIsLoadingAssociatedEnvs] = useState(false);
  const [selectedPromoteEnvId, setSelectedPromoteEnvId] = useState('');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  const fetchMediaImages = useCallback(async (options?: { force?: boolean }) => {
    if (!selectedPilotId) {
      setAssets([]);
      setJourneyCounts(null);
      setAssetsError(null);
      failedFetchKeyRef.current = null;
      return;
    }

    const fetchKey = `${selectedPilotId}:${currentAppEnv}`;

    if (!options?.force && failedFetchKeyRef.current === fetchKey) {
      return;
    }

    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setIsLoadingAssets(true);
    if (options?.force) {
      failedFetchKeyRef.current = null;
    }
    setAssetsError(null);

    try {
      const result = await MediaAPI.getImages(rc, {
        project_id: String(selectedPilotId),
        environment: toMediaApiEnvironmentParam(currentAppEnv) as MediaEnvironment,
      });

      if (!result.success) {
        failedFetchKeyRef.current = fetchKey;
        setAssets([]);
        setJourneyCounts(null);
        const message = result.error ?? 'Failed to load media images';
        setAssetsError(message);
        toastRef.current.error(message);
      } else {
        failedFetchKeyRef.current = null;
        setAssets(result.assets);
        setJourneyCounts(result.response?.counts ?? null);
      }
    } finally {
      isFetchingRef.current = false;
      setIsLoadingAssets(false);
    }
  }, [rc, selectedPilotId, currentAppEnv]);

  useEffect(() => {
    void fetchMediaImages();
  }, [selectedPilotId, currentAppEnv, fetchMediaImages]);

  const fetchAssociatedEnvironments = useCallback(async (projectId: string) => {
    if (!projectId) {
      setAssociatedEnvironments([]);
      return;
    }

    setIsLoadingAssociatedEnvs(true);
    try {
      const response = await PilotApis.getAssociatedEnvironments(rc, projectId);
      if (response.error) {
        setAssociatedEnvironments([]);
        toastRef.current.error(response.error.message || 'Failed to fetch associated environments');
        return;
      }

      const environments = (response.data?.data ?? []) as ProjectEnvironment[];
      setAssociatedEnvironments(environments);
    } catch (error) {
      setAssociatedEnvironments([]);
      toastRef.current.error(error instanceof Error ? error.message : 'Failed to fetch associated environments');
    } finally {
      setIsLoadingAssociatedEnvs(false);
    }
  }, [rc]);

  useEffect(() => {
    if (selectedPilotId) {
      void fetchAssociatedEnvironments(String(selectedPilotId));
    } else {
      setAssociatedEnvironments([]);
    }
  }, [selectedPilotId, fetchAssociatedEnvironments]);

  useEffect(() => {
    if (selectedAsset && selectedPilotId) {
      void fetchAssociatedEnvironments(String(selectedPilotId));
    }
  }, [selectedAsset?.assetId, selectedPilotId, fetchAssociatedEnvironments]);

  useEffect(() => {
    setSelectedPromoteEnvId('');
  }, [selectedAsset?.assetId, selectedPilotId, currentAppEnv]);

  const selectedPilot = useMemo(
    () => pilotList.find((p) => String(p.projectId) === String(selectedPilotId)) as
      | { projectId?: string; name?: string; environment_id?: string | number | null; projectEnvironments?: ProjectEnvironment[] }
      | undefined,
    [pilotList, selectedPilotId],
  );

  const effectiveAssociatedEnvironments = useMemo(() => {
    if (associatedEnvironments.length > 0) return associatedEnvironments;
    return Array.isArray(selectedPilot?.projectEnvironments) ? selectedPilot.projectEnvironments : [];
  }, [associatedEnvironments, selectedPilot?.projectEnvironments]);

  useEffect(() => {
    if (!selectedAsset || !selectedPilotId) {
      setDrawerEnvUrls({});
      setIsLoadingDrawerEnvUrls(false);
      return;
    }

    const configKey = selectedAsset.assetId;
    const configType = selectedAsset.productLine;
    const drawerEnvs = getComparisonEnvOrder(currentAppEnv);
    setDrawerEnvUrls({ [currentAppEnv]: selectedAsset.fileURL });

    if (!configKey || !configType) {
      setIsLoadingDrawerEnvUrls(false);
      return;
    }

    if (isLoadingAssociatedEnvs) {
      setIsLoadingDrawerEnvUrls(true);
      return;
    }

    const envsToFetch = drawerEnvs.filter(
      (env) =>
        env !== currentAppEnv &&
        isDrawerEnvironmentAvailable(env, currentAppEnv, effectiveAssociatedEnvironments),
    );

    if (envsToFetch.length === 0) {
      setIsLoadingDrawerEnvUrls(false);
      return;
    }

    let cancelled = false;
    setIsLoadingDrawerEnvUrls(true);

    void (async () => {
      const byAppEnv = mapAssociatedEnvironmentsByAppEnv(effectiveAssociatedEnvironments);
      const updates: Partial<Record<AppEnvironment, string>> = {};

      await Promise.all(
        envsToFetch.map(async (env) => {
          const projectEnv = byAppEnv[env];
          const apiHost = projectEnv ? getProjectEnvironmentApiHost(projectEnv) : undefined;
          const result = await MediaAPI.getUnresolvedConfigValue(rc, {
            pilot_id: String(selectedPilotId),
            config_key: configKey,
            config_type: configType,
            ...(projectEnv?.id != null ? { env_id: projectEnv.id } : {}),
            environment: toMediaApiEnvironmentParam(env),
            ...(apiHost ? { api_host: apiHost } : {}),
          });
          if (cancelled || !result.success) return;

          if (result.notFound) {
            updates[env] = '';
            return;
          }

          if (result.value) {
            updates[env] = result.value;
          }
        }),
      );

      if (!cancelled) {
        setDrawerEnvUrls((prev) => ({ ...prev, ...updates }));
        setIsLoadingDrawerEnvUrls(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    selectedAsset?.assetId,
    selectedAsset?.productLine,
    selectedPilotId,
    currentAppEnv,
    rc,
    isLoadingAssociatedEnvs,
    effectiveAssociatedEnvironments,
  ]);

  const projectName = selectedPilot?.name || 'Efficiency Manitoba';

  const allCategoriesForJourney = useMemo(() => {
    const journeyAssets = assets.filter((a) => a.journeyTab === activeJourney);
    const uniqueCategories = Array.from(new Set(journeyAssets.map((a) => a.category)));
    const allCategory = uniqueCategories.filter((cat) => isAllCategory(cat));
    const rest = uniqueCategories.filter((cat) => !isAllCategory(cat));

    if (activeJourney === 'brand') {
      const order = ['LOGO', 'LOGOTYPE', 'SHC MASCOTS', 'APPLIANCES', 'SOCIAL MEDIA ICONS'];
      rest.sort((a, b) => {
        const indexA = order.indexOf(a.toUpperCase());
        const indexB = order.indexOf(b.toUpperCase());
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return a.localeCompare(b);
      });
    } else {
      rest.sort((a, b) => a.localeCompare(b));
    }

    return [...rest, ...allCategory];
  }, [assets, activeJourney]);

  useEffect(() => {
    setShowOnlyDrift(false);
    setViewMode('snapshot');
    setCompareAssetEnvUrls({});
    setAssetsWithEnvDrift(new Set());
  }, [selectedPilotId, activeJourney]);

  const getCategoryAssets = useCallback(
    (category: string) =>
      assets.filter(
        (a) =>
          a.journeyTab === activeJourney &&
          a.category === category &&
          (!showOnlyDrift || (a.assetId != null && assetsWithEnvDrift.has(a.assetId))) &&
          (a.name.toLowerCase().includes(search.toLowerCase()) ||
            a.assetId.toLowerCase().includes(search.toLowerCase())),
      ),
    [assets, activeJourney, showOnlyDrift, assetsWithEnvDrift, search],
  );

  const visibleCategoriesWithAssets = useMemo(
    () =>
      allCategoriesForJourney
        .filter((cat) => selectedCategories.includes(cat))
        .filter((cat) => getCategoryAssets(cat).length > 0),
    [allCategoriesForJourney, selectedCategories, getCategoryAssets],
  );

  useEffect(() => {
    const firstCategory = visibleCategoriesWithAssets[0];
    setExpandedSections(firstCategory ? new Set([firstCategory]) : new Set());
  }, [visibleCategoriesWithAssets]);

  const toggleSection = (category: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const fetchEnvDiffForJourney = useCallback(async (): Promise<boolean> => {
    if (!selectedPilotId) return false;

    if (isLoadingAssociatedEnvs) {
      toast.info('Loading associated environments…');
      return false;
    }

    const journeyAssets = assets.filter((a) => a.journeyTab === activeJourney);
    const targetConfigs = journeyAssets
      .filter((a) => a.assetId && a.productLine)
      .map((a) => ({ configType: a.productLine, configKey: a.assetId }));

    if (targetConfigs.length === 0) {
      toast.warning('No configs available to compare for this journey.');
      return false;
    }

    const byAppEnv = mapAssociatedEnvironmentsByAppEnv(effectiveAssociatedEnvironments);
    const drawerEnvs = getComparisonEnvOrder(currentAppEnv);
    const envsToCompare = drawerEnvs.filter(
      (env) =>
        env !== currentAppEnv &&
        isDrawerEnvironmentAvailable(env, currentAppEnv, effectiveAssociatedEnvironments),
    );

    const currentProjectEnv = byAppEnv[currentAppEnv];
    const currentEnvIdRaw = currentProjectEnv?.id ?? selectedPilot?.environment_id;
    const currentEnvId = currentEnvIdRaw != null ? Number(currentEnvIdRaw) : NaN;

    if (!Number.isFinite(currentEnvId)) {
      toast.error('Current environment ID is missing. Check associated environments.');
      return false;
    }

    const targetEnvIds: number[] = [];
    for (const env of envsToCompare) {
      const envId = byAppEnv[env]?.id;
      if (envId == null) continue;
      const numericId = Number(envId);
      if (Number.isFinite(numericId)) {
        targetEnvIds.push(numericId);
      }
    }

    if (targetEnvIds.length === 0) {
      toast.warning('No comparable environments available. Please add required env to use this feature.');
      return false;
    }

    setIsLoadingEnvDiff(true);
    try {
      const result = await MediaAPI.getEnvMatrix(rc, {
        pilotId: String(selectedPilotId),
        currentEnvId,
        targetEnvIds,
        targetConfigs,
      });

      if (!result.success || !result.response) {
        toast.error(result.error ?? 'Failed to load environment differences');
        return false;
      }

      const updates = envMatrixConfigsToCompareUrls(result.response.configs, currentAppEnv);
      const driftIds = new Set<string>(
        result.response.configs.filter((config) => !config.allTargetsMatch).map((config) => config.configKey),
      );

      for (const asset of journeyAssets) {
        if (!asset.assetId) continue;
        if (!updates[asset.assetId]) {
          updates[asset.assetId] = { [currentAppEnv]: asset.fileURL ?? '' };
        } else if (updates[asset.assetId][currentAppEnv] === undefined) {
          updates[asset.assetId][currentAppEnv] = asset.fileURL ?? '';
        }
      }

      setCompareAssetEnvUrls(updates);
      setAssetsWithEnvDrift(driftIds);
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to load environment differences');
      return false;
    } finally {
      setIsLoadingEnvDiff(false);
    }
  }, [
    selectedPilotId,
    assets,
    activeJourney,
    currentAppEnv,
    effectiveAssociatedEnvironments,
    isLoadingAssociatedEnvs,
    selectedPilot?.environment_id,
    rc,
    toast,
  ]);

  const handleEnvDiffToggle = useCallback(async () => {
    if (showOnlyDrift) {
      setShowOnlyDrift(false);
      setViewMode('snapshot');
      return;
    }

    const success = await fetchEnvDiffForJourney();
    if (success) {
      setShowOnlyDrift(true);
      setViewMode('compare');
    }
  }, [showOnlyDrift, fetchEnvDiffForJourney]);

  useEffect(() => {
    setSelectedCategories(allCategoriesForJourney);
  }, [allCategoriesForJourney]);

  const toggleCategory = (cat: string) => {
    setSelectedCategories((prev) => (prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]));
  };

  const handleSelectAll = () => setSelectedCategories(allCategoriesForJourney);
  const handleClearAll = () => setSelectedCategories([]);

  const handleCopyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const applyUploadedImageUrl = useCallback((assetId: string, url: string) => {
    setAssets((prev) =>
      prev.map((asset) =>
        asset.assetId === assetId ? { ...asset, fileURL: url, thumbnailURL: url } : asset,
      ),
    );
    setSelectedAsset((prev) =>
      prev && prev.assetId === assetId ? { ...prev, fileURL: url, thumbnailURL: url } : prev,
    );
  }, []);

  const applyDrawerEnvImageUrl = useCallback((env: AppEnvironment, url: string) => {
    setDrawerEnvUrls((prev) => ({ ...prev, [env]: url }));
  }, []);

  const handlePromoteToProjectEnv = useCallback(
    async (targetEnv: ProjectEnvironment, options?: { configValue?: string; asset?: MediaAssetVersion }) => {
      const asset = options?.asset ?? selectedAsset;
      if (!asset || !selectedPilotId || isPromoting) return;

      const configKey = asset.assetId;
      const configType = asset.productLine;
      if (!configKey || !configType) {
        toast.error('Missing config key or config type for this asset');
        return;
      }

      if (targetEnv.id == null || String(targetEnv.id) === '') {
        toast.error('Invalid target environment');
        return;
      }

      const envId = Number(targetEnv.id);
      if (!Number.isFinite(envId)) {
        toast.error('Invalid target environment id');
        return;
      }

      const configValue = (
        options?.configValue ??
        compareAssetEnvUrls[configKey]?.[currentAppEnv] ??
        drawerEnvUrls[currentAppEnv] ??
        asset.fileURL
      )?.trim();
      if (!configValue) {
        toast.error('Missing config value to promote');
        return;
      }

      setIsPromoting(true);
      try {
        const result = await MediaAPI.promoteConfig(rc, {
          pilotId: String(selectedPilotId),
          configType,
          configKey,
          configValue,
          envId,
        });

        if (!result.success) {
          toast.error(result.error ?? 'Failed to promote config');
          return;
        }

        const targetLabel = getProjectEnvironmentDisplayLabel(targetEnv);
        const promotedUrl = result.response?.configValue;
        const targetAppEnv = projectEnvironmentToAppEnv(targetEnv);

        if (targetAppEnv && promotedUrl) {
          if (selectedAsset?.assetId === configKey) {
            applyDrawerEnvImageUrl(targetAppEnv, promotedUrl);
          }

          setCompareAssetEnvUrls((prev) => {
            const assetUrls = {
              ...(prev[configKey] ?? { [currentAppEnv]: asset.fileURL ?? configValue }),
              [targetAppEnv]: promotedUrl,
            };
            return { ...prev, [configKey]: assetUrls };
          });

          const updatedUrls = {
            ...(compareAssetEnvUrls[configKey] ?? { [currentAppEnv]: asset.fileURL ?? configValue }),
            [targetAppEnv]: promotedUrl,
          };
          const baseline = updatedUrls[currentAppEnv] ?? configValue;
          const drawerEnvs = getComparisonEnvOrder(currentAppEnv);
          const stillDrift = assetHasEnvDriftFromUrls(baseline, currentAppEnv, updatedUrls, drawerEnvs);
          setAssetsWithEnvDrift((prev) => {
            const next = new Set(prev);
            if (stillDrift) {
              next.add(configKey);
            } else {
              next.delete(configKey);
            }
            return next;
          });
        }

        if (result.response?.pushedToDataserver) {
          toast.success(`Promoted to ${targetLabel}`);
        } else {
          toast.warning(`Promoted to ${targetLabel}, but sync to dataserver failed`);
        }
      } finally {
        setIsPromoting(false);
      }
    },
    [
      selectedAsset,
      selectedPilotId,
      compareAssetEnvUrls,
      drawerEnvUrls,
      currentAppEnv,
      isPromoting,
      rc,
      toast,
      applyDrawerEnvImageUrl,
    ],
  );

  const handlePromoteBetweenAppEnvs = useCallback(
    (sourceEnv: AppEnvironment, targetAppEnv: AppEnvironment) => {
      const targetEnv = mapAssociatedEnvironmentsByAppEnv(effectiveAssociatedEnvironments)[targetAppEnv];
      if (!targetEnv) {
        toast.error(`No associated project environment found for ${formatAppEnvLabel(targetAppEnv)}`);
        return;
      }

      const configValue = getDrawerEnvImageUrl(
        sourceEnv,
        currentAppEnv,
        drawerEnvUrls,
        selectedAsset?.fileURL,
      )?.trim();

      if (!configValue) {
        toast.error('Missing config value to promote');
        return;
      }

      void handlePromoteToProjectEnv(targetEnv, { configValue });
    },
    [
      effectiveAssociatedEnvironments,
      drawerEnvUrls,
      currentAppEnv,
      selectedAsset?.fileURL,
      handlePromoteToProjectEnv,
      toast,
    ],
  );

  const handlePromoteToAppEnv = useCallback(
    (target: AppEnvironment, asset?: MediaAssetVersion) => {
      const targetEnv = mapAssociatedEnvironmentsByAppEnv(effectiveAssociatedEnvironments)[target];
      if (!targetEnv) {
        toast.error(`No associated project environment found for ${formatAppEnvLabel(target)}`);
        return;
      }

      const promoteAsset = asset ?? selectedAsset;
      if (!promoteAsset) {
        toast.error('No asset selected for promotion');
        return;
      }

      const configKey = promoteAsset.assetId;
      const configValue = (
        (configKey ? compareAssetEnvUrls[configKey]?.[currentAppEnv] : undefined) ??
        promoteAsset.fileURL ??
        drawerEnvUrls[currentAppEnv]
      )?.trim();

      if (!configValue) {
        toast.error('Missing config value to promote');
        return;
      }

      void handlePromoteToProjectEnv(targetEnv, { asset: promoteAsset, configValue });
    },
    [
      effectiveAssociatedEnvironments,
      selectedAsset,
      compareAssetEnvUrls,
      currentAppEnv,
      drawerEnvUrls,
      handlePromoteToProjectEnv,
      toast,
    ],
  );

  const handleUploadClick = () => {
    if (!selectedAsset || !selectedPilotId || isUploading) return;
    uploadFileInputRef.current?.click();
  };

  const handleUploadFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file || !selectedAsset || !selectedPilotId) return;

    if (!isAcceptedMediaFile(file)) {
      toast.error('Unsupported file type. Use .png, .jpg, .jpeg, .gif, .svg, .webp, or .ico');
      return;
    }

    const configKey = selectedAsset.assetId;
    const configType = selectedAsset.productLine;
    if (!configKey || !configType) {
      toast.error('Missing config key or config type for this asset');
      return;
    }

    setIsUploading(true);
    try {
      const result = await MediaAPI.upload(rc, {
        pilotId: String(selectedPilotId),
        configKey,
        configType,
        file,
        environment: CURRENT_MEDIA_ENV,
        ...(selectedAsset.dimensionLookupCode
          ? { imageDimension: selectedAsset.dimensionLookupCode }
          : {}),
      });

      if (!result.success || !result.response) {
        toast.error(result.error ?? 'Failed to upload image');
        return;
      }

      applyDrawerEnvImageUrl(currentAppEnv, result.response.url);
      applyUploadedImageUrl(configKey, result.response.url);

      const uploadedEnvLabel = formatAppEnvLabel(currentAppEnv, { lowercase: currentAppEnv === 'dev' });
      if (result.response.pushed_to_dataserver) {
        toast.success(`Image uploaded and synced to ${uploadedEnvLabel}`);
      } else {
        toast.warning(`Image uploaded to ${uploadedEnvLabel}, but sync to dataserver failed. The file is available at the new URL.`);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleRevertToDefault = async () => {
    if (!selectedAsset || !selectedPilotId || isReverting || isUploading) return;

    const configKey = selectedAsset.assetId;
    const configType = selectedAsset.productLine;
    if (!configKey || !configType) {
      toast.error('Missing config key or config type for this asset');
      return;
    }

    setIsReverting(true);
    try {
      const result = await MediaAPI.revertConfig(rc, {
        projectId: String(selectedPilotId),
        configKey,
        configType,
      });

      if (!result.success) {
        toast.error(result.error ?? 'Failed to revert to default');
        return;
      }

      if (!result.response) {
        toast.error('Failed to revert to default');
        return;
      }

      applyDrawerEnvImageUrl(currentAppEnv, result.response.defaultValue);
      applyUploadedImageUrl(configKey, result.response.defaultValue);

      if (result.response.pushed_to_dataserver) {
        toast.success(`Reverted to default on ${currentEnvLabel}`);
      } else {
        toast.warning(`Reverted to default on ${currentEnvLabel}, but sync to dataserver failed.`);
      }
    } finally {
      setIsReverting(false);
    }
  };

  const currentEnvLabel = formatAppEnvLabel(currentAppEnv, { lowercase: currentAppEnv === 'dev' });
  const envLabel = viewMode === 'compare' ? 'ENV COMPARE' : currentEnvLabel;
  const drawerEnvOrder = useMemo(() => getComparisonEnvOrder(currentAppEnv), [currentAppEnv]);
  const missingDrawerEnvs = useMemo(
    () =>
      new Set(
        isLoadingAssociatedEnvs
          ? []
          : getDrawerEnvironmentsMissingApiHost(
              drawerEnvOrder,
              currentAppEnv,
              effectiveAssociatedEnvironments,
            ),
      ),
    [drawerEnvOrder, currentAppEnv, effectiveAssociatedEnvironments, isLoadingAssociatedEnvs],
  );
  const promoteTargets = useMemo(() => getPromoteTargets(currentAppEnv), [currentAppEnv]);
  const promotionDropdownEnvironments = useMemo(
    () =>
      getPromotionDropdownEnvironments(effectiveAssociatedEnvironments, {
        currentAppEnv,
        currentEnvironmentId: selectedPilot?.environment_id ?? null,
      }),
    [effectiveAssociatedEnvironments, currentAppEnv, selectedPilot?.environment_id],
  );

  useEffect(() => {
    if (promotionDropdownEnvironments.length === 0) {
      setSelectedPromoteEnvId('');
      return;
    }

    const stillValid = promotionDropdownEnvironments.some((env) => String(env.id) === selectedPromoteEnvId);
    if (!stillValid) {
      setSelectedPromoteEnvId(String(promotionDropdownEnvironments[0].id));
    }
  }, [promotionDropdownEnvironments, selectedPromoteEnvId]);

  const handlePromoteFromDropdown = useCallback(() => {
    const targetEnv = promotionDropdownEnvironments.find((env) => String(env.id) === selectedPromoteEnvId);
    if (!targetEnv) return;
    void handlePromoteToProjectEnv(targetEnv);
  }, [handlePromoteToProjectEnv, promotionDropdownEnvironments, selectedPromoteEnvId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Header */}
      <Box sx={{ px: 4, py: 3, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, typography: 'body2', color: 'text.secondary' }}>
              <Typography variant="body2" color="text.secondary">Media Content</Typography>
              <ChevronRightIcon sx={{ fontSize: 14 }} />
              <Typography variant="body2" color="text.secondary">{projectName}</Typography>
              <ChevronRightIcon sx={{ fontSize: 14 }} />
              <Chip label={envLabel} size="small" sx={{ height: 22, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }} />
            </Box>

            {selectedPilotId && assets.length > 0 && (
              <Button
                onClick={() => void handleEnvDiffToggle()}
                disabled={isLoadingEnvDiff || isLoadingAssociatedEnvs}
                startIcon={isLoadingEnvDiff ? <CircularProgress size={16} color="inherit" /> : <WarningAmberIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 2,
                  py: 1,
                  bgcolor: showOnlyDrift ? alpha('#f59e0b', 0.2) : '#fff4e5',
                  color: showOnlyDrift ? '#92400e' : '#8a5100',
                  border: 1,
                  borderColor: showOnlyDrift ? '#fbbf24' : '#ffd5a1',
                  boxShadow: showOnlyDrift ? `0 0 0 4px ${alpha('#f59e0b', 0.15)}` : 1,
                  '&:hover': { bgcolor: showOnlyDrift ? alpha('#f59e0b', 0.25) : '#ffe8cc' },
                }}
              >
                {isLoadingEnvDiff ? 'Checking…' : showOnlyDrift ? 'Exit env compare' : 'Env differences'}
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h5" fontWeight={700}>Media Content Snapshot</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* <Button variant="outlined" startIcon={<LocalOfferOutlinedIcon />} onClick={() => toast.info('Full Catalog — coming soon')} sx={{ fontWeight: 700, textTransform: 'none' }}>
                Full Catalog
              </Button> */}
              <TextField
                size="small"
                placeholder="Search keywords..."
                value={search}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ width: 256, '& .MuiOutlinedInput-root': { borderRadius: 999, bgcolor: alpha(theme.palette.grey[100], 1) } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          <Tabs
            value={activeJourney}
            onChange={(_, val: JourneyTab) => setActiveJourney(val)}
            sx={{
              minHeight: 48,
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 48, gap: 1 },
              '& .MuiTabs-indicator': { height: 3 },
            }}
          >
            {JOURNEY_TABS.map((tab) => {
              const count = journeyCounts?.[tab.countKey];
              const label = count != null ? `${tab.label} (${count})` : tab.label;
              return (
                <Tab key={tab.id} value={tab.id} label={label} icon={tab.icon} iconPosition="start" />
              );
            })}
          </Tabs>
        </Box>
      </Box>

      {/* Toolbar */}
      <Box sx={{ px: 4, py: 2, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 20, boxShadow: 1 }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<FilterListIcon color="primary" />}
            endIcon={<ExpandMoreIcon />}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
            sx={{ fontWeight: 700, textTransform: 'none', minWidth: 240, justifyContent: 'space-between' }}
          >
            Filter Sections ({selectedCategories.length})
          </Button>
        </Box>
      </Box>

      <Popover
        open={Boolean(filterAnchor)}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 300, mt: 1 } } }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: alpha(theme.palette.grey[100], 0.5), display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" fontWeight={800} letterSpacing="0.1em" color="text.secondary">AVAILABLE SECTIONS</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" onClick={handleSelectAll} sx={{ minWidth: 0, fontSize: 10, fontWeight: 700 }}>All</Button>
            <Button size="small" onClick={handleClearAll} sx={{ minWidth: 0, fontSize: 10, fontWeight: 700, color: 'text.secondary' }}>None</Button>
          </Box>
        </Box>
        <Box sx={{ maxHeight: 288, overflow: 'auto', p: 0.5 }}>
          {allCategoriesForJourney.map((cat) => (
            <Box
              key={cat}
              onClick={() => toggleCategory(cat)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: alpha(theme.palette.grey[100], 1) } }}
            >
              <Checkbox checked={selectedCategories.includes(cat)} size="small" sx={{ p: 0.5 }} />
              <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>{getCategoryDisplayLabel(cat)}</Typography>
              <Chip label={assets.filter((a) => a.journeyTab === activeJourney && a.category === cat).length} size="small" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
            </Box>
          ))}
        </Box>
      </Popover>

      {/* Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
        <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 10 }}>
          {!selectedPilotId && (
            <Box sx={{ py: 12, textAlign: 'center' }}>
              <Typography color="text.secondary">Select a project to view media content.</Typography>
            </Box>
          )}

          {selectedPilotId && isLoadingAssets && (
            <Box sx={{ py: 12, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={36} />
              <Typography color="text.secondary">Loading media images…</Typography>
            </Box>
          )}

          {selectedPilotId && !isLoadingAssets && assetsError && (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="error" sx={{ mb: 2 }}>{assetsError}</Typography>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => void fetchMediaImages({ force: true })}>
                Retry
              </Button>
            </Box>
          )}

          {selectedPilotId && !isLoadingAssets && !assetsError && assets.length === 0 && (
            <Box sx={{ py: 12, textAlign: 'center' }}>
              <Typography color="text.secondary">No media images found for this project and environment.</Typography>
              <Button variant="text" startIcon={<RefreshIcon />} onClick={() => void fetchMediaImages({ force: true })} sx={{ mt: 1 }}>
                Refresh
              </Button>
            </Box>
          )}

          {selectedPilotId && !isLoadingAssets && !assetsError && assets.length > 0 && visibleCategoriesWithAssets.map((category) => {
              const categoryAssets = getCategoryAssets(category);
              const isExpanded = expandedSections.has(category);

              const renderSectionContent = () => {
                if (category.toUpperCase() === 'SHC MASCOTS' && activeJourney === 'brand') {
                  return (
                    <MascotMatrix
                      category={category}
                      items={categoryAssets}
                      viewMode={viewMode}
                      showOnlyDrift={showOnlyDrift}
                      currentAppEnv={currentAppEnv}
                      assetsWithEnvDrift={assetsWithEnvDrift}
                      compareAssetEnvUrls={compareAssetEnvUrls}
                      onSelect={setSelectedAsset}
                      onPromote={handlePromoteToAppEnv}
                    />
                  );
                }

                if (category.toUpperCase() === 'APPLIANCES' && activeJourney === 'brand') {
                  return (
                    <ApplianceGrid
                      category={category}
                      items={categoryAssets}
                      viewMode={viewMode}
                      showOnlyDrift={showOnlyDrift}
                      currentAppEnv={currentAppEnv}
                      assetsWithEnvDrift={assetsWithEnvDrift}
                      compareAssetEnvUrls={compareAssetEnvUrls}
                      onSelect={setSelectedAsset}
                      onToast={(title, description) => toast.info(description ? `${title}: ${description}` : title)}
                      onPromote={handlePromoteToAppEnv}
                    />
                  );
                }

                return viewMode === 'snapshot' ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(6, 1fr)', xl: 'repeat(8, 1fr)' }, gap: 3 }}>
                    {categoryAssets.map((asset) => (
                      <Card
                        key={asset.id}
                        variant="outlined"
                        onClick={() => setSelectedAsset(asset)}
                        sx={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', '&:hover': { borderColor: 'primary.main', boxShadow: 4 } }}
                      >
                        <Box sx={{ aspectRatio: '1', bgcolor: '#dedede', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1, position: 'relative' }}>
                          <Box component="img" src={asset.thumbnailURL} alt={asset.name} sx={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain', opacity: 0.85 }} />
                        </Box>
                        <CardContent sx={{ p: 1.5, textAlign: 'center', borderTop: 1, borderColor: 'divider', '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="caption" fontWeight={700} color='black' noWrap display="block">{asset.name}</Typography>
                          <Typography variant="caption" fontWeight={300} color="text.secondary" sx={{ fontFamily: 'monospace', fontSize: '10px' }}>{asset.dimensions}</Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {categoryAssets.map((asset) => (
                      <ComparisonRow
                        key={asset.id}
                        asset={asset}
                        currentAppEnv={currentAppEnv}
                        envUrls={asset.assetId ? compareAssetEnvUrls[asset.assetId] : undefined}
                        onPromote={(target) => handlePromoteToAppEnv(target, asset)}
                      />
                    ))}
                  </Box>
                );
              };

              const getSectionSubtitle = () => {
                if (category.toUpperCase() === 'SHC MASCOTS' && activeJourney === 'brand') {
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {showOnlyDrift ? 'Showing only mascots with environmental mismatches.' : '3 scenarios per utility type. Renders on cover and comparison tiles.'}
                    </Typography>
                  );
                }

                if (category.toUpperCase() === 'APPLIANCES' && activeJourney === 'brand') {
                  const driftCount = categoryAssets.filter((a) => a.assetId && assetsWithEnvDrift.has(a.assetId)).length;
                  return (
                    <Typography variant="body2" color="text.secondary">
                      {showOnlyDrift
                        ? `${driftCount} items with drift detected.`
                        : `${categoryAssets.length} icons · Used in disaggregation breakdowns and recommendations.`}
                    </Typography>
                  );
                }

                return undefined;
              };

              const getSectionActions = () => {
                if (category.toUpperCase() === 'APPLIANCES' && activeJourney === 'brand' && !showOnlyDrift) {
                  return (
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <Button variant="outlined" size="small" startIcon={<RotateLeftIcon />} onClick={() => toast.info('Reset Initiated: Restoring all appliances to global standard...')} sx={{ fontWeight: 700 }}>
                        Revert all to default
                      </Button>
                      <Button variant="contained" size="small" startIcon={<CloudUploadIcon />} sx={{ fontWeight: 700 }}>
                        Bulk upload ZIP
                      </Button>
                    </Box>
                  );
                }

                return undefined;
              };

              return (
                <CollapsibleCategorySection
                  key={category}
                  category={category}
                  expanded={isExpanded}
                  onToggle={() => toggleSection(category)}
                  showOnlyDrift={showOnlyDrift}
                  count={categoryAssets.length}
                  subtitle={getSectionSubtitle()}
                  headerActions={getSectionActions()}
                >
                  {renderSectionContent()}
                </CollapsibleCategorySection>
              );
            })}

          {selectedPilotId && !isLoadingAssets && !assetsError && assets.length > 0 && (selectedCategories.length === 0 || (showOnlyDrift && assetsWithEnvDrift.size === 0)) && (
            <Box sx={{ py: 12, textAlign: 'center', border: '2px dashed', borderColor: 'divider', borderRadius: 4, bgcolor: alpha(theme.palette.grey[100], 0.3) }}>
              <FilterListIcon sx={{ fontSize: 64, color: alpha(theme.palette.grey[400], 0.5), mb: 2 }} />
              <Typography variant="h6" fontWeight={700}>
                {showOnlyDrift ? 'No mismatches detected' : 'No sections selected'}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1 }}>
                {showOnlyDrift
                  ? 'Everything is in sync across all environments.'
                  : 'Please use the filter utility above to pick the sections you want to review.'}
              </Typography>
              <Button onClick={showOnlyDrift ? () => void handleEnvDiffToggle() : handleSelectAll} sx={{ mt: 2, fontWeight: 700 }}>
                {showOnlyDrift ? 'View all assets' : 'Show all sections'}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Asset detail drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedAsset)}
        onClose={() => setSelectedAsset(null)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 580 },
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderLeft: 1,
            borderColor: 'divider',
          },
        }}
      >
        {selectedAsset && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Chip label={selectedAsset.type} size="small" variant="outlined" sx={{ fontWeight: 800, letterSpacing: '0.08em', fontSize: 10 }} />
                <IconButton size="small" onClick={() => setSelectedAsset(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography variant="h5" fontWeight={700}>{selectedAsset.name}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Detailed view and cross-environment status for this asset.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                <Typography
                  component="code"
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.grey[100], 1),
                    color: 'text.primary',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedAsset.registryMapping}
                </Typography>
                <IconButton size="small" onClick={() => handleCopyText(selectedAsset.registryMapping, 'Key')}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${drawerEnvOrder.length}, 1fr)`, gap: 2, mb: 4 }}>
                {(() => {
                  const baselineUrl = drawerEnvUrls[currentAppEnv] ?? selectedAsset.fileURL ?? '';
                  return drawerEnvOrder.map((env, idx) => {
                  const nextEnv = idx < drawerEnvOrder.length - 1;
                  const isPromoteTarget = promoteTargets.includes(env);
                  const envColumnLabel = formatAppEnvLabel(env, { lowercase: env === currentAppEnv && currentAppEnv === 'dev' });
                  const isMissingEnv = missingDrawerEnvs.has(env);
                  const hasResolvedEnvUrl = env in drawerEnvUrls;
                  const envImageUrl = getDrawerEnvImageUrl(env, currentAppEnv, drawerEnvUrls, selectedAsset.fileURL);
                  const isConfigNotFound = hasResolvedEnvUrl && envImageUrl === '';
                  const envDrift = drawerEnvHasDrift(env, currentAppEnv, drawerEnvUrls, baselineUrl);
                  const connectorDrift = nextEnv
                    ? drawerEnvConnectorHasDrift(
                        env,
                        drawerEnvOrder[idx + 1],
                        currentAppEnv,
                        drawerEnvUrls,
                        selectedAsset.fileURL,
                      )
                    : false;
                  const nextAppEnv = nextEnv ? drawerEnvOrder[idx + 1] : undefined;
                  const nextEnvMissing = nextAppEnv ? missingDrawerEnvs.has(nextAppEnv) : false;
                  const canPromoteViaConnector =
                    Boolean(nextAppEnv) &&
                    !nextEnvMissing &&
                    !isMissingEnv &&
                    !isConfigNotFound &&
                    Boolean(mapAssociatedEnvironmentsByAppEnv(effectiveAssociatedEnvironments)[nextAppEnv!]);
                  const isLoadingEnv =
                    !isMissingEnv &&
                    !isConfigNotFound &&
                    env !== currentAppEnv &&
                    (isLoadingAssociatedEnvs || (isLoadingDrawerEnvUrls && !hasResolvedEnvUrl));
                  return (
                    <Box key={env} sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        letterSpacing="0.1em"
                        color="text.secondary"
                        display="block"
                        sx={{ mb: 1, ...(env === currentAppEnv && currentAppEnv === 'dev' ? { textTransform: 'lowercase' } : {}) }}
                      >
                        {envColumnLabel}
                      </Typography>
                      <Box sx={{ position: 'relative' }}>
                        <Box
                          sx={{
                            aspectRatio: '1',
                            borderRadius: 3,
                            border: 1,
                            borderColor: isMissingEnv
                              ? alpha(theme.palette.error.main, 0.35)
                              : isConfigNotFound
                                ? alpha(theme.palette.warning.main, 0.35)
                              : envDrift && isPromoteTarget
                                ? '#fcd34d'
                                : 'divider',
                            bgcolor: isMissingEnv
                              ? alpha(theme.palette.error.main, 0.04)
                              : isConfigNotFound
                                ? alpha(theme.palette.warning.main, 0.06)
                              : envDrift && isPromoteTarget
                                ? alpha('#f59e0b', 0.08)
                                : theme.palette.mode === 'dark'
                                  ? alpha(theme.palette.common.white, 0.05)
                                  : alpha(theme.palette.grey[100], 1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 2,
                          }}
                        >
                          {isMissingEnv ? (
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 1, lineHeight: 1.4 }}>
                              {MISSING_DRAWER_ENV_MESSAGE}
                            </Typography>
                          ) : isConfigNotFound ? (
                            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ px: 1, lineHeight: 1.4 }}>
                              {CONFIG_VALUE_NOT_FOUND_MESSAGE}
                            </Typography>
                          ) : isLoadingEnv ? (
                            <CircularProgress size={24} />
                          ) : envImageUrl ? (
                            <Box
                              component="img"
                              src={envImageUrl}
                              alt=""
                              sx={{
                                maxHeight: '100%',
                                maxWidth: '100%',
                                objectFit: 'contain',
                                filter: envDrift && isPromoteTarget ? 'grayscale(1)' : 'none',
                                opacity: envDrift && isPromoteTarget ? 0.5 : 1,
                              }}
                            />
                          ) : null}
                        </Box>
                        {nextEnv && !isMissingEnv && !isConfigNotFound && (
                          <Tooltip
                            title={
                              canPromoteViaConnector
                                ? connectorDrift
                                  ? `Promote to ${formatAppEnvLabel(nextAppEnv!)}`
                                  : `Re-sync to ${formatAppEnvLabel(nextAppEnv!)}`
                                : 'Target environment is not available'
                            }
                          >
                            <span>
                              <IconButton
                                size="small"
                                disabled={!canPromoteViaConnector || isPromoting}
                                onClick={() => {
                                  if (!nextAppEnv) return;
                                  handlePromoteBetweenAppEnvs(env, nextAppEnv);
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: -16,
                                  transform: 'translateY(-50%)',
                                  zIndex: 1,
                                  height: 32,
                                  width: 32,
                                  bgcolor: 'background.paper',
                                  border: 2,
                                  borderColor: connectorDrift ? alpha('#f59e0b', 0.4) : alpha('#22c55e', 0.4),
                                  color: connectorDrift ? '#f59e0b' : '#16a34a',
                                  '&:hover': {
                                    bgcolor: connectorDrift ? alpha('#f59e0b', 0.12) : alpha('#22c55e', 0.12),
                                  },
                                  '&.Mui-disabled': {
                                    bgcolor: 'background.paper',
                                    borderColor: 'divider',
                                    color: 'text.disabled',
                                  },
                                }}
                              >
                                {isPromoting ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : connectorDrift ? (
                                  <WarningAmberIcon fontSize="small" />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        fontWeight={800}
                        sx={{
                          mt: 1,
                          display: 'block',
                          color: isMissingEnv ? 'error.main' : isConfigNotFound ? 'warning.main' : envDrift && isPromoteTarget ? '#d97706' : 'text.secondary',
                        }}
                      >
                        {isMissingEnv ? 'ENV NOT CONFIGURED' : isConfigNotFound ? 'CONFIG NOT FOUND' : envDrift && isPromoteTarget ? 'DRIFT DETECTED' : 'GLOBAL DEFAULT'}
                      </Typography>
                    </Box>
                  );
                });
                })()}
              </Box>

              <input
                ref={uploadFileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.gif,.svg,.webp,.ico,image/*"
                hidden
                onChange={handleUploadFileChange}
              />

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={isUploading ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                disabled={isUploading || !selectedPilotId}
                onClick={handleUploadClick}
                sx={{ mb: 2, fontWeight: 700 }}
              >
                {isUploading ? 'UPLOADING....' : `UPLOAD NEW IMAGE (${currentEnvLabel})`}
              </Button>
              <Box sx={{ display: 'flex', gap: 2, mb: 4, alignItems: 'flex-start' }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="media-promote-env-label" shrink={Boolean(selectedPromoteEnvId) || isLoadingAssociatedEnvs || promotionDropdownEnvironments.length === 0}>
                    Promote to environment
                  </InputLabel>
                  <Select
                    labelId="media-promote-env-label"
                    value={selectedPromoteEnvId}
                    label="Promote to environment"
                    onChange={(event) => setSelectedPromoteEnvId(event.target.value)}
                    disabled={isLoadingAssociatedEnvs}
                    displayEmpty
                    notched={Boolean(selectedPromoteEnvId) || isLoadingAssociatedEnvs || promotionDropdownEnvironments.length === 0}
                    renderValue={(value) => {
                      if (isLoadingAssociatedEnvs) return 'Loading environments…';
                      if (!value) {
                        return (
                          <Typography variant="body2" color="text.secondary">
                            {promotionDropdownEnvironments.length === 0 ? 'No associated environments' : 'Select environment'}
                          </Typography>
                        );
                      }
                      const env = promotionDropdownEnvironments.find((item) => String(item.id) === value);
                      return env ? getProjectEnvironmentDisplayLabel(env) : 'Select environment';
                    }}
                  >
                    {promotionDropdownEnvironments.length === 0 ? (
                      <MenuItem disabled value="">
                        {isLoadingAssociatedEnvs ? 'Loading environments…' : 'No associated environments found for this project'}
                      </MenuItem>
                    ) : (
                      promotionDropdownEnvironments.map((env) => (
                        <MenuItem key={String(env.id)} value={String(env.id)}>
                          {getProjectEnvironmentDisplayLabel(env)}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  onClick={handlePromoteFromDropdown}
                  disabled={!selectedPromoteEnvId || isLoadingAssociatedEnvs || isPromoting || !selectedAsset}
                  sx={{ minWidth: 120, fontWeight: 700, fontSize: 11, letterSpacing: '0.05em', whiteSpace: 'nowrap', mt: 0.25 }}
                >
                  {isPromoting ? 'Promoting…' : 'Promote'}
                </Button>
              </Box>

              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <InfoOutlinedIcon fontSize="small" color="action" />
                <Typography variant="caption" fontWeight={800} letterSpacing="0.1em" color="text.secondary">ASSET METADATA</Typography>
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="caption" fontWeight={800} letterSpacing="0.08em" color="text.secondary" display="block">IMAGE NAME</Typography>
                  <Typography variant="body2" fontWeight={700}>{selectedAsset.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" fontWeight={800} letterSpacing="0.08em" color="text.secondary" display="block">DIMENSIONS</Typography>
                  <Typography variant="body2" fontWeight={700}>{selectedAsset.dimensions}</Typography>
                </Box>
              </Box>

              <Typography variant="caption" fontWeight={800} letterSpacing="0.08em" color="text.secondary" display="block" sx={{ mb: 1 }}>
                SECTION MAPPING
              </Typography>
              <Box
                sx={{
                  bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.black, 0.25) : alpha(theme.palette.grey[900], 0.04),
                  borderRadius: 3,
                  p: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  component="code"
                  variant="caption"
                  sx={{
                    fontFamily: 'monospace',
                    color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : 'text.secondary',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {selectedAsset.sectionMapping}
                </Typography>
                <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={() => handleCopyText(selectedAsset.sectionMapping, 'Mapping')}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
                bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.03) : alpha(theme.palette.grey[100], 0.5),
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Button
                startIcon={isReverting ? <CircularProgress size={16} color="inherit" /> : <RotateLeftIcon />}
                disabled={isReverting || isUploading || !selectedPilotId}
                onClick={() => void handleRevertToDefault()}
                sx={{ fontWeight: 700, fontSize: 12, color: 'text.primary' }}
              >
                {isReverting ? 'Reverting…' : `Revert to default (${currentEnvLabel})`}
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default MediaContentPage;
